import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { HoverTip } from 'components/HoverTip'
import {
  IMailPostage,
  IMailPurposeConfig,
  KNOWN_MAIL_PURPOSES,
  mailPurposeLabel,
} from 'pages/Servicing/PrintMail/types'
import { useMemo, useState } from 'react'
import { Button } from 'stories/components'

import { extraServiceOptions, mailTypeOptions } from './constants'

/**
 * Per-communication postage, keyed by the same string a piece carries as its `purpose`.
 *
 * This is what makes the service general rather than a tax-form button: a tenant turns mailing on
 * for `statement` or `lateNotice` here and sets its postage, and the platform did not have to learn
 * a new document type to allow it. A purpose that is not listed still gets the defaults when a
 * single letter is sent by hand - the switch gates the bulk runs and the "Mail this" action.
 */
export const PurposesTable = ({
  purposes,
  defaults,
  onChange,
}: {
  purposes: Record<string, IMailPurposeConfig>
  defaults: IMailPostage
  onChange: (next: Record<string, IMailPurposeConfig>) => void
}) => {
  const [adding, setAdding] = useState('')
  const [custom, setCustom] = useState('')

  const keys = useMemo(() => Object.keys(purposes).sort(), [purposes])
  const addable = useMemo(
    () => Object.keys(KNOWN_MAIL_PURPOSES).filter((key) => !(key in purposes)),
    [purposes],
  )

  const set = (purpose: string, patch: IMailPurposeConfig) => {
    onChange({ ...purposes, [purpose]: { ...(purposes[purpose] || {}), ...patch } })
  }

  /**
   * Stop offering a purpose and drop its overrides, so it follows the defaults again.
   *
   * Not a delete: `PUT /print-mail/config` merges what it is sent into what is stored, so a key
   * left out of the payload is kept rather than removed. A button that made the row disappear
   * until the next load and then brought it back would be a lie, so this does the thing the API
   * can actually do and says so.
   */
  const reset = (purpose: string) => {
    onChange({ ...purposes, [purpose]: { enabled: false } })
  }

  const add = () => {
    const key = (adding === '__custom' ? custom : adding).trim()
    if (!key) return
    if (key in purposes) return
    onChange({ ...purposes, [key]: { enabled: true } })
    setAdding('')
    setCustom('')
  }

  /** '' on an override means "use the default", which is not the same as "none". */
  const inherited = (value: any, fallback: any) => (value === undefined ? fallback : value)

  return (
    <div>
      <h3 className="text-lg font-bold mb-1">Per-communication postage</h3>
      <p className="text-sm text-gray-600 mb-3">
        Which letters this company is willing to post, and how each one goes out. Anything left unset follows the
        defaults above. Turning a purpose off does not delete anything already queued under it, and a purpose that is
        off still follows the defaults if someone posts a one-off letter under it by hand.
      </p>

      {!keys.length && (
        <p className="text-sm text-gray-500 mb-3">
          Nothing configured, so every purpose follows the defaults and no bulk run or &quot;Mail this&quot; action is
          offered. Add the communications you intend to post.
        </p>
      )}

      {!!keys.length && (
        <div className="overflow-auto border border-gray-200 rounded mb-3">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Communication</th>
                <th className="px-3 py-2 text-left font-semibold">Offered</th>
                <th className="px-3 py-2 text-left font-semibold">Mail class</th>
                <th className="px-3 py-2 text-left font-semibold">Extra service</th>
                <th className="px-3 py-2 text-center font-semibold">Colour</th>
                <th className="px-3 py-2 text-center font-semibold">Duplex</th>
                <th className="px-3 py-2 text-center font-semibold">Reply env.</th>
                <th className="px-3 py-2">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((purpose) => {
                const row = purposes[purpose] || {}
                const mailType = inherited(row.mailType, defaults.mailType)
                return (
                  <tr key={purpose} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <div>{mailPurposeLabel(purpose)}</div>
                      <div className="text-[11px] text-gray-400 font-mono">{purpose}</div>
                    </td>
                    <td className="px-3 py-2">
                      <label className="flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={row.enabled === true}
                          onChange={(e) => set(purpose, { enabled: e.target.checked })}
                        />
                        <span className="text-xs text-gray-600">{row.enabled ? 'Yes' : 'No'}</span>
                      </label>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="border rounded px-1.5 py-1 text-xs"
                        value={row.mailType ?? ''}
                        onChange={(e) => set(purpose, { mailType: e.target.value || undefined })}
                      >
                        <option value="">Default ({mailTypeOptions[defaults.mailType]})</option>
                        {Object.entries(mailTypeOptions).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <HoverTip
                        message={
                          mailType === 'usps_standard'
                            ? 'Standard post carries no extra service; the provider rejects one outright.'
                            : ''
                        }
                      >
                        <select
                          className="border rounded px-1.5 py-1 text-xs disabled:bg-gray-100"
                          disabled={mailType === 'usps_standard'}
                          value={row.extraService ?? '__default'}
                          onChange={(e) =>
                            set(purpose, {
                              extraService: e.target.value === '__default' ? undefined : e.target.value,
                            })
                          }
                        >
                          <option value="__default">
                            Default ({extraServiceOptions[defaults.extraService] || 'None'})
                          </option>
                          {Object.entries(extraServiceOptions).map(([value, label]) => (
                            <option key={value || 'none'} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </HoverTip>
                    </td>
                    <TriStateCell
                      value={row.color}
                      fallback={defaults.color}
                      onChange={(value) => set(purpose, { color: value })}
                    />
                    <TriStateCell
                      value={row.doubleSided}
                      fallback={defaults.doubleSided}
                      onChange={(value) => set(purpose, { doubleSided: value })}
                    />
                    <TriStateCell
                      value={row.returnEnvelope}
                      fallback={defaults.returnEnvelope}
                      onChange={(value) => set(purpose, { returnEnvelope: value })}
                    />
                    <td className="px-3 py-2 text-right">
                      <HoverTip message="Stop offering this and clear its overrides, so it follows the defaults">
                        <button aria-label="Reset purpose" className="text-slate-500" onClick={() => reset(purpose)}>
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                      </HoverTip>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select className="border rounded px-2 py-1 text-sm" value={adding} onChange={(e) => setAdding(e.target.value)}>
          <option value="">Add a communication...</option>
          {addable.map((key) => (
            <option key={key} value={key}>
              {KNOWN_MAIL_PURPOSES[key]}
            </option>
          ))}
          <option value="__custom">Something else...</option>
        </select>
        {adding === '__custom' && (
          <input
            className="border rounded px-2 py-1 text-sm"
            placeholder="purposeKey"
            value={custom}
            onChange={(e) => setCustom(e.target.value.replace(/[^A-Za-z0-9_]/g, ''))}
          />
        )}
        <Button color="gray" className="mb-0" onClick={add} disabled={!adding || (adding === '__custom' && !custom)}>
          Add
        </Button>
      </div>
    </div>
  )
}

/**
 * Yes / No / follow the default.
 *
 * A plain checkbox cannot say "unset", and unset is the useful state: a tenant who later changes
 * their default colour setting expects every purpose that never overrode it to change with it.
 */
const TriStateCell = ({
  value,
  fallback,
  onChange,
}: {
  value: boolean | undefined
  fallback: boolean
  onChange: (value: boolean | undefined) => void
}) => (
  <td className="px-3 py-2 text-center">
    <select
      className="border rounded px-1.5 py-1 text-xs"
      value={value === undefined ? '' : value ? 'yes' : 'no'}
      onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value === 'yes')}
    >
      <option value="">Default ({fallback ? 'yes' : 'no'})</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  </td>
)
